import { backendApi } from "@/services/backend/api";
import { updateCache } from "@/services/backend/_lib/update-cache";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import {
  Component,
  Kind,
} from "@/services/backend/rlt/treatments/types/component";
import { SegmentKind } from "@/services/backend/rlt/treatments/types/segmentKind";
import { Treatment } from "@/services/backend/rlt/treatments/types/treatment";
import { Filter } from "@/shared/lib/filter/filter";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { DocumentState } from "@/services/backend/documents/types/state";

const treatmentApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltTreatmentCreate: build.mutation<Treatment, CreateRequest>({
      query: (request) => ({
        url: "/rlt/treatment",
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "RltTreatment", id: "LIST" }],
    }),
    rltTreatmentCreateFromOld: build.mutation<Treatment, CreateFromOldRequest>({
      query: (request) => ({
        url: "/rlt/treatment/create-from-old",
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "RltTreatment", id: "LIST" }],
    }),
    rltTreatmentShow: build.query<Treatment, ShowRequest>({
      query: (request) => ({
        url: `/rlt/treatment`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltTreatment", id: result.id }] : [],
    }),
    rltTreatmentPatch: build.mutation<Treatment, PatchRequest>({
      query: (request) => ({
        url: `/rlt/treatment`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        { type: "RltTreatment", id: "LIST" },
      ],
    }),
    rltTreatmentResetReferenceNumber: build.mutation<
      Treatment,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltTreatment", id: request.id },
        { type: "RltTreatment", id: "LIST" },
      ],
    }),
    rltTreatmentList: build.query<Lst<Treatment>, Partial<ListRequest>>({
      query: (request) => ({
        url: "/rlt/treatment/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "RltTreatment"),
    }),
    rltTreatmentListAvailableComponentKinds: build.query<
      AvailableComponentKinds,
      ListAvailableComponentKindsRequest
    >({
      query: (request) => ({
        url: "/rlt/treatment/list-available-component-kinds",
        method: "GET",
        params: request,
      }),
    }),
    rltTreatmentCopyBaseData: build.mutation<Treatment, CopyBaseDataRequest>({
      query: (request) => ({
        url: "/rlt/treatment/copy-base-data",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.destinationId },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentAddArea: build.mutation<Treatment, AddAreaRequest>({
      query: (request) => ({
        url: `/rlt/treatment/area`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentRenameArea: build.mutation<Treatment, RenameAreaRequest>({
      query: (request) => ({
        url: `/rlt/treatment/area`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentDeleteArea: build.mutation<Treatment, DeleteAreaRequest>({
      query: (request) => ({
        url: `/rlt/treatment/area`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentAddPosition: build.mutation<Treatment, AddPositionRequest>({
      query: (request) => ({
        url: `/rlt/treatment/position`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentCopyPosition: build.mutation<Treatment, CopyPositionRequest>({
      query: (request) => ({
        url: `/rlt/treatment/position/copy`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentDeletePosition: build.mutation<
      Treatment,
      DeletePositionRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/position`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateComponent: build.mutation<
      Treatment,
      UpdateComponentRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/position/component`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateCycle: build.mutation<Treatment, UpdateCycleRequest>({
      query: (request) => ({
        url: `/rlt/treatment/position/cycle`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateFormula: build.mutation<Treatment, UpdateFormulaRequest>({
      query: (request) => ({
        url: `/rlt/treatment/position/formula`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateComment: build.mutation<Treatment, UpdateCommentRequest>({
      query: (request) => ({
        url: `/rlt/treatment/position/comment`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateCycleDiscountActive: build.mutation<
      Treatment,
      UpdateCycleDiscountActiveRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/position/cycle-discount-active`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateCycleDiscountActiveAll: build.mutation<
      Treatment,
      UpdateCycleDiscountActiveAllRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/position/cycle-discount-active-all`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateCycleDiscountPercentage: build.mutation<
      Treatment,
      UpdateCycleDiscountPercentageRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/position/cycle-discount-percentage`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateSpecialDiscountActive: build.mutation<
      Treatment,
      UpdateSpecialDiscountActiveRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/position/special-discount-active`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateSpecialDiscountAmount: build.mutation<
      Treatment,
      UpdateSpecialDiscountAmountRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/position/special-discount-amount`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdatePositionCompletion: build.mutation<
      Treatment,
      UpdatePositionCompletion
    >({
      query: (request) => ({
        url: `/rlt/treatment/position/completion`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateActuallyTreated: build.mutation<
      Treatment,
      UpdateActuallyTreatedRequest
    >({
      query: (request) => ({
        url: `/rlt/treatment/position/actually-treated`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateState: build.mutation<Treatment, UpdateStateRequest>({
      query: (request) => ({
        url: `/rlt/treatment/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltTreatmentUpdateRemark: build.mutation<Treatment, UpdateRemarkRequest>({
      query: (request) => ({
        url: `/rlt/treatment/remark`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
      ],
    }),
    rltTreatmentDelete: build.mutation<Treatment, DeleteRequest>({
      query: (request) => ({
        url: `/rlt/treatment}`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltTreatment", id: "LIST" },
        { type: "RltTreatment", id: request.id },
        "RltCalculation",
        "RltConfirmation",
        "RltContract",
        "RltPricing",
        "RltPricingByArea",
        "RltOffer",
        "RltOrder",
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
  }),
});

export const {
  useRltTreatmentCreateMutation,
  useRltTreatmentCreateFromOldMutation,
  useRltTreatmentShowQuery,
  useRltTreatmentPatchMutation,
  useRltTreatmentResetReferenceNumberMutation,
  useRltTreatmentListQuery,
  useRltTreatmentListAvailableComponentKindsQuery,
  useRltTreatmentCopyBaseDataMutation,
  useRltTreatmentAddAreaMutation,
  useRltTreatmentRenameAreaMutation,
  useRltTreatmentDeleteAreaMutation,
  useRltTreatmentAddPositionMutation,
  useRltTreatmentCopyPositionMutation,
  useRltTreatmentDeletePositionMutation,
  useRltTreatmentUpdateComponentMutation,
  useRltTreatmentUpdateCycleMutation,
  useRltTreatmentUpdateFormulaMutation,
  useRltTreatmentUpdateCommentMutation,
  useRltTreatmentUpdateCycleDiscountActiveMutation,
  useRltTreatmentUpdateCycleDiscountActiveAllMutation,
  useRltTreatmentUpdateCycleDiscountPercentageMutation,
  useRltTreatmentUpdateSpecialDiscountActiveMutation,
  useRltTreatmentUpdateSpecialDiscountAmountMutation,
  useRltTreatmentUpdatePositionCompletionMutation,
  useRltTreatmentUpdateActuallyTreatedMutation,
  useRltTreatmentUpdateRemarkMutation,
  useRltTreatmentUpdateStateMutation,
  useRltTreatmentDeleteMutation,
} = treatmentApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
  ownerType: string;
  ownerId: string;
}

export interface CreateFromOldRequest {
  oldId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
  ownerType: string;
  ownerId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  ownerType?: Filter<string>;
  owner?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export type ListAvailableComponentKindsRequest = object;

export interface AvailableComponentKinds {
  [segmentKind: string]: Kind[];
}

export interface CopyBaseDataRequest {
  destinationId: string;
  sourceId: string;
}

export interface AddAreaRequest {
  id: string;
  segment: SegmentKind;
  area: string;
}

export interface RenameAreaRequest {
  id: string;
  segment: SegmentKind;
  old: string;
  new: string;
}

export interface DeleteAreaRequest {
  id: string;
  segment: SegmentKind;
  area: string;
}

export interface AddPositionRequest {
  id: string;
  positionId: string;
  parentId?: string;
  segment: SegmentKind;
  area: string;
  componentKind: Kind;
}

export interface CopyPositionRequest {
  id: string;
  oldPositionId: string;
  newPositionId: string;
  newArea: string;
}

export interface DeletePositionRequest {
  id: string;
  positionId: string;
}

export interface UpdateComponentRequest {
  id: string;
  positionId: string;
  component: Component;
}

export interface UpdateCycleRequest {
  id: string;
  positionId: string;
  cycle: number;
}

export interface UpdateFormulaRequest {
  id: string;
  positionId: string;
  component: Component;
}

export interface UpdateCommentRequest {
  id: string;
  positionId: string;
  comment: string;
}

export interface UpdateCycleDiscountActiveRequest {
  id: string;
  positionId: string;
  active: boolean;
}

export interface UpdateCycleDiscountActiveAllRequest {
  id: string;
  active: boolean;
}

export interface UpdateCycleDiscountPercentageRequest {
  id: string;
  positionId: string;
  percentage: number;
}

export interface UpdateSpecialDiscountActiveRequest {
  id: string;
  positionId: string;
  active: boolean;
}

export interface UpdateSpecialDiscountAmountRequest {
  id: string;
  positionId: string;
  amount: number;
}

export interface UpdateActuallyTreatedRequest {
  id: string;
  positionId: string;
  component: Component;
}

export interface UpdatePositionCompletion {
  id: string;
  positionId: string;
  completed: boolean;
  forceCompletion: boolean;
}

export interface UpdateRemarkRequest {
  id: string;
  remark: string;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface DeleteRequest {
  id: string;
}

export const updateRltTreatmentCache = updateCache(
  treatmentApi,
  "rltTreatmentShow",
);
